import type { FC } from 'react';
import React, { useCallback } from 'react';
import cx from 'classnames';
import { MDText } from 'i18n-react';

import { MultiSelect } from '@motorway/motorway-storybook-cra';

import useVehicleFilterStore, {
	useFilters as useStoreFilters,
} from 'Stores/FilterStore/VehicleListFilter/VehicleListFilterStore';
import { APP_THEME } from 'Utilities/consts';

import texts from '../../VehicleListV2.texts.json';
import { DropdownPanel } from '../DropdownPanel';
import { useMakeModelFilter } from '../hooks/useMakeModelFilter';

import styles from './MakeModelDropdown.module.scss';

const LocalT = new MDText(texts);

interface MakeModelDropdownProps {
	onClose?: () => void;
}

export const MakeModelDropdown: FC<MakeModelDropdownProps> = ({ onClose }) => {
	const setValue = useVehicleFilterStore((store) => store.setValue);
	const storeFilters = useStoreFilters();

	const handleCommit = useCallback(
		(makes: string[], models: number[]) => {
			setValue({ id: 'make', value: makes });
			setValue({ id: 'model', value: models });
		},
		[setValue],
	);

	const {
		formattedMakes,
		formattedModels,
		handleMakeChange,
		handleModelChange,
		isModelDisabled,
		localMake,
		localModel,
		makeOptionsChangedCount,
		makeValues,
		modelOptionsChangedCount,
		modelValues,
	} = useMakeModelFilter({ autoCommit: false });

	const handleApply = useCallback(() => {
		handleCommit(localMake, localModel);
		onClose?.();
	}, [handleCommit, localMake, localModel, onClose]);

	const handleClear = useCallback(() => {
		handleCommit([], []);
		onClose?.();
	}, [handleCommit, onClose]);

	const hasChanged =
		localMake.length !== storeFilters.make.length ||
		localModel.length !== storeFilters.model.length ||
		localMake.some((v) => !storeFilters.make.includes(v)) ||
		localModel.some((v) => !storeFilters.model.includes(v));

	return (
		<DropdownPanel
			clearDisabled={localMake.length === 0 && localModel.length === 0}
			hasChanged={hasChanged}
			hasStoreValue={storeFilters.make.length > 0 || storeFilters.model.length > 0}
			onApply={handleApply}
			onClear={handleClear}
		>
			<span className={styles.dropdownTitle}>{`${LocalT.translate('filterBar.makeModel')}`}</span>
			<div className={styles.makeModelSelects}>
				<MultiSelect
					key={`make-${makeOptionsChangedCount}`}
					searchBar
					className={cx('mw-input-select mw-input-multiselect mw-input-multiselect-dark', styles.multiSelect)}
					id="popover-multiselect-make"
					inputProps={{}}
					onChange={handleMakeChange}
					options={formattedMakes}
					placeholder={`${LocalT.translate('filterOptions.anyMake')}`}
					theme={APP_THEME}
					value={makeValues}
				/>
				<MultiSelect
					key={modelOptionsChangedCount}
					searchBar
					className={cx('mw-input-select mw-input-multiselect mw-input-multiselect-dark', styles.multiSelect)}
					id="popover-multiselect-model"
					inputProps={{ disabled: isModelDisabled }}
					onChange={handleModelChange}
					options={formattedModels}
					placeholder={`${LocalT.translate('filterOptions.anyModel')}`}
					theme={APP_THEME}
					value={modelValues}
				/>
			</div>
		</DropdownPanel>
	);
};
